import { expect } from './expect';
import { Http, IHttp } from './http';
import { Log } from './interfaces/log.interface';

export enum Severity {
  Debug = 1,
  Verbose = 2,
  Info = 3,
  Warning = 4,
  Error = 5,
  Critical = 6,
}

export interface ILogger {
  writeLog(data: string | object, severity: Severity): Promise<void>;

  writeDebugLog(data: string | object): Promise<void>;

  writeVerboseLog(data: string | object): Promise<void>;

  writeInfoLog(data: string | object): Promise<void>;

  writeWarningLog(data: string | object): Promise<void>;

  writeErrorLog(data: string | object): Promise<void>;

  writeCriticalLog(data: string | object): Promise<void>;
}

export class DataTrackingServiceLogger {
  private readonly url: string;
  private readonly http: IHttp;

  constructor(url: string, dependencies?: { http: IHttp }) {
    this.url = expect(url).toBeDefined(ErrorMessage.UrlIsRequired);
    this.http = dependencies?.http ?? new Http();
  }

  public async writeLog(data: string | object, severity: Severity = Severity.Info): Promise<void> {
    try {
      let log: Log = {
        severity: severity,
      };
      if (typeof data === 'string') {
        log.message = data as string;
      } else {
        log = {
          ...log,
          ...data,
        };
      }

      await this.http.post(this.url, log);
    } catch (error) {
      console.error(`DataTrackingServiceLogger::writeLog() - error occured: ${error.message}`, error);
    }
  }

  public async writeDebugLog(data: string | object): Promise<void> {
    return this.writeLog(data, Severity.Debug);
  }

  public async writeVerboseLog(data: string | object): Promise<void> {
    return this.writeLog(data, Severity.Verbose);
  }

  public async writeInfoLog(data: string | object): Promise<void> {
    return this.writeLog(data, Severity.Info);
  }

  public async writeWarningLog(data: string | object): Promise<void> {
    return this.writeLog(data, Severity.Warning);
  }

  public async writeErrorLog(data: string | object): Promise<void> {
    return this.writeLog(data, Severity.Error);
  }

  public async writeCriticalLog(data: string | object): Promise<void> {
    return this.writeLog(data, Severity.Critical);
  }
}

enum ErrorMessage {
  UrlIsRequired = 'The URL of the logging server is required to initialize DataTrackingServiceLogger.',
}

export class HiddenLogger {
  private readonly logs = [];
  private readonly logsLimit = 100;

  constructor() {}

  public async writeLog(data: string | object, severity: Severity = Severity.Info): Promise<void> {
    try {
      let log: Log = {
        timestamp: Date.now(),
        severity: severity,
      };
      if (typeof data === 'string') {
        log.message = data as string;
      } else {
        log = {
          ...log,
          ...data,
        };
      }

      this.logs.push(log);
      if (this.logs.length > this.logsLimit) {
        this.logs.shift();
      }

      return Promise.resolve();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // do nothing, no one ever see this error
    }
  }

  public async writeDebugLog(data: string | object): Promise<void> {
    return this.writeLog(data, Severity.Debug);
  }

  public async writeVerboseLog(data: string | object): Promise<void> {
    return this.writeLog(data, Severity.Verbose);
  }

  public async writeInfoLog(data: string | object): Promise<void> {
    return this.writeLog(data, Severity.Info);
  }

  public async writeWarningLog(data: string | object): Promise<void> {
    return this.writeLog(data, Severity.Warning);
  }

  public async writeErrorLog(data: string | object): Promise<void> {
    return this.writeLog(data, Severity.Error);
  }

  public async writeCriticalLog(data: string | object): Promise<void> {
    return this.writeLog(data, Severity.Critical);
  }
}

export class ConsoleLogger {
  private readonly debugOnly: boolean;

  public constructor(debugOnly?: boolean) {
    this.debugOnly = debugOnly ?? false;
  }

  public async writeLog(data: string | object, severity: Severity = Severity.Info): Promise<void> {
    try {
      if (this.debugOnly) {
        console.debug(data);
      } else {
        switch (severity) {
          case Severity.Debug:
            console.debug(data);
            break;
          case Severity.Verbose:
            console.trace(data);
            break;
          case Severity.Info:
            console.info(data);
            break;
          case Severity.Warning:
            console.warn(data);
            break;
          case Severity.Error:
            console.error(data);
            break;
          case Severity.Critical:
            console.error(data);
            break;

          default:
            break;
        }
      }

      return Promise.resolve();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // do nothing, no one ever see this error
    }
  }

  public async writeDebugLog(data: string | object): Promise<void> {
    return this.writeLog(data, Severity.Debug);
  }

  public async writeVerboseLog(data: string | object): Promise<void> {
    return this.writeLog(data, Severity.Verbose);
  }

  public async writeInfoLog(data: string | object): Promise<void> {
    return this.writeLog(data, Severity.Info);
  }

  public async writeWarningLog(data: string | object): Promise<void> {
    return this.writeLog(data, Severity.Warning);
  }

  public async writeErrorLog(data: string | object): Promise<void> {
    return this.writeLog(data, Severity.Error);
  }

  public async writeCriticalLog(data: string | object): Promise<void> {
    return this.writeLog(data, Severity.Critical);
  }
}
